<template>
  <div class="bg-white p-4 h-full text-black">
    <h2 class="font-bold fs-24 mb-2">{{ $t('Cấu hình API đối tác') }}</h2>
    <p class="txt-grey-700 mb-4">
      {{ $t('Thiết lập API key bên thứ 3 theo từng đối tác tích hợp của Hodo.') }}
    </p>

    <div v-loading="loading">
      <div
        v-for="customer in customers"
        :key="customer.id"
        class="border rounded-lg mb-3 overflow-hidden"
      >
        <div
          class="px-4 py-3 bg-blue-gray d-flex align-items-center justify-content-between cursor-pointer"
          @click="toggleExpand(customer.id)"
        >
          <div>
            <div class="fw-700 fs-16">{{ customer.name }}</div>
            <div class="fs-13 txt-grey-700">{{ customer.code }}</div>
          </div>
          <i
            class="el-icon-arrow-down transition-all duration-200"
            :class="{ 'rotate-180': isExpanded(customer.id) }"
          />
        </div>

        <div v-show="isExpanded(customer.id)" class="p-4">
          <div class="row fs-13 fw-600 txt-grey-900 mb-2">
            <div class="col-md-3 mb-2 mb-md-0">{{ $t('Tên API key') }}</div>
            <div class="col-md-7 mb-2 mb-md-0">{{ $t('API key') }}</div>
            <div class="col-md-2">{{ $t('Thao tác') }}</div>
          </div>

          <template v-if="customer.api_config && !isEditing(customer.id)">
            <div class="row fs-14 align-items-center">
              <div class="col-md-3 mb-2 mb-md-0">{{ customer.api_config.name }}</div>
              <div class="col-md-7 mb-2 mb-md-0">
                <code class="cs-key">{{ customer.api_config.api_key }}</code>
              </div>
              <div class="col-md-2 d-flex align-items-center">
                <el-button
                  size="mini"
                  type="primary"
                  plain
                  :disabled="!canConfig || isSaving(customer.id)"
                  @click="startEdit(customer)"
                >
                  {{ $t('lbl_edit') || 'Edit' }}
                </el-button>
                <el-button
                  size="mini"
                  type="danger"
                  plain
                  class="ml-2"
                  :disabled="!canConfig || isSaving(customer.id)"
                  @click="onDelete(customer)"
                >
                  {{ 'Delete' }}
                </el-button>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="row">
              <div class="col-md-3 mb-2 mb-md-0">
                <el-input
                  v-model="draftMap[customer.id].name"
                  :disabled="!canConfig || isSaving(customer.id)"
                  :placeholder="$t('Ví dụ: Superdoc key')"
                />
              </div>
              <div class="col-md-7 mb-2 mb-md-0">
                <el-input
                  v-model="draftMap[customer.id].api_key"
                  :disabled="!canConfig || isSaving(customer.id)"
                  :placeholder="$t('Nhập API key')"
                />
              </div>
              <div class="col-md-2 d-flex align-items-center">
                <el-button
                  size="mini"
                  type="primary"
                  :loading="isSaving(customer.id)"
                  :disabled="!canConfig"
                  @click="onSave(customer)"
                >
                  {{ $t('lbl_save') || 'Save' }}
                </el-button>
                <el-button
                  v-if="customer.api_config"
                  size="mini"
                  plain
                  class="ml-2"
                  :disabled="isSaving(customer.id)"
                  @click="cancelEdit(customer)"
                >
                  {{ $t('lbl_cancel') || 'Cancel' }}
                </el-button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import orgIntegrationApiKeyService from '@/services/orgIntegrationApiKeyService'
import { APP_MODE } from '@/utils/constants'
import { setOrgApiKeyStatusGlobal } from '@/utils/orgApiKeyStatusUtils'

export default {
  name: 'OrganizationPartnerApiConfig',
  data () {
    return {
      loading: false,
      customers: [],
      expandedIds: [],
      editingMap: {},
      draftMap: {},
      savingMap: {}
    }
  },
  computed: {
    canConfig () {
      const isOrgMode = Number(this.$appMode) === APP_MODE.organization
      // const isOwner = Number(this.$globalOrg?.owner_id) === Number(this.$user?.id)
      const isOwner = true
      return isOrgMode && isOwner
    }
  },
  created () {
    this.fetchCustomers()
  },
  methods: {
    isExpanded (id) {
      return this.expandedIds.includes(id)
    },
    toggleExpand (id) {
      if (this.isExpanded(id)) {
        this.expandedIds = this.expandedIds.filter((item) => item !== id)
        return
      }
      this.expandedIds = this.expandedIds.concat(id)
    },
    isEditing (id) {
      return !!this.editingMap[id]
    },
    isSaving (id) {
      return !!this.savingMap[id]
    },
    async fetchCustomers () {
      const orgId = this.$globalOrg?.id
      if (!orgId) return
      this.loading = true
      try {
        this.customers = await orgIntegrationApiKeyService.getCustomersWithApiConfig({ orgId })
        setOrgApiKeyStatusGlobal({ orgId, providers: this.customers })
        this.customers.forEach((item) => {
          this.$set(this.draftMap, item.id, {
            name: item.api_config?.name || '',
            api_key: item.api_config?.api_key || ''
          })
        })
      } catch (error) {
        console.log(error)
        this.$toast && this.$toast.open({ message: this.$t('lbl_api_error_status') || 'An error occurs when update status. Please try another time.', type: 'error' })
      } finally {
        this.loading = false
      }
    },
    startEdit (customer) {
      if (!this.canConfig) return
      this.$set(this.editingMap, customer.id, true)
      this.$set(this.draftMap, customer.id, {
        name: customer.api_config?.name || '',
        api_key: customer.api_config?.api_key || ''
      })
    },
    cancelEdit (customer) {
      this.$set(this.editingMap, customer.id, false)
      this.$set(this.draftMap, customer.id, {
        name: customer.api_config?.name || '',
        api_key: customer.api_config?.api_key || ''
      })
    },
    async onSave (customer) {
      if (!this.canConfig) return
      const draft = this.draftMap[customer.id] || {}
      const keyName = (draft.name || '').trim()
      const apiKey = (draft.api_key || '').trim()

      if (!keyName || !apiKey) {
        this.$toast && this.$toast.open({ message: this.$t('Vui lòng nhập đủ tên API key và API key.'), type: 'warning' })
        return
      }

      const orgId = this.$globalOrg?.id
      if (!orgId) return
      this.$set(this.savingMap, customer.id, true)

      try {
        const apiConfig = await orgIntegrationApiKeyService.saveCustomerApiKey({
          orgId,
          customerId: customer.id,
          configId: customer.api_config?.id,
          name: keyName,
          apiKey
        })
        customer.api_config = apiConfig
        setOrgApiKeyStatusGlobal({ orgId, providers: this.customers })
        this.$set(this.editingMap, customer.id, false)
        this.$toast && this.$toast.open({ message: this.$t('Lưu cấu hình thành công.'), type: 'success' })
      } catch (error) {
        console.log(error)
        this.$toast && this.$toast.open({ message: this.$t('lbl_api_error_status') || 'An error occurs when update status. Please try another time.', type: 'error' })
      } finally {
        this.$set(this.savingMap, customer.id, false)
      }
    },
    async onDelete (customer) {
      if (!this.canConfig) return
      const ok = confirm(this.$t('Bạn có chắc chắn muốn xóa API key của đối tác này không?'))
      if (!ok) return

      const orgId = this.$globalOrg?.id
      if (!orgId) return
      this.$set(this.savingMap, customer.id, true)
      try {
        await orgIntegrationApiKeyService.deleteCustomerApiKey({
          configId: customer.api_config?.id
        })
        customer.api_config = null
        setOrgApiKeyStatusGlobal({ orgId, providers: this.customers })
        this.$set(this.editingMap, customer.id, false)
        this.$set(this.draftMap, customer.id, { name: '', api_key: '' })
        this.$toast && this.$toast.open({ message: this.$t('Xóa API key thành công.'), type: 'success' })
      } catch (error) {
        console.log(error)
        this.$toast && this.$toast.open({ message: this.$t('lbl_api_error_status') || 'An error occurs when update status. Please try another time.', type: 'error' })
      } finally {
        this.$set(this.savingMap, customer.id, false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.cs-key {
  background: #f4f6f8;
  padding: 6px 10px;
  border-radius: 8px;
  display: inline-block;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.rotate-180 {
  transform: rotate(180deg);
}

.transition-all {
  transition: all 0.2s ease;
}
</style>
